import { useNavigate } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useSettings } from '../settings/SettingsContext';
import { Loading, LoadError } from '../components/Loading';
import Crumbs from '../components/Crumbs';

/**
 * The whole catalog, for picking a language to add to the home screen.
 *
 * This is the list the home screen used to be. Choosing one adds it to
 * `studyingLanguages` and returns — the learner lands back on a home screen
 * that now has it, rather than on a language page they have to navigate out of.
 */
export default function AddLanguagePage() {
  const api = useApi();
  const navigate = useNavigate();
  const { settings, update } = useSettings();

  const languages = useAsync(() => catalogApi.languages(api), [api]);

  const add = (code: string) => {
    // The settings update is optimistic, so the home screen has it on arrival.
    if (!settings.studyingLanguages.includes(code)) {
      update({ studyingLanguages: [...settings.studyingLanguages, code] });
    }
    navigate('/');
  };

  return (
    <main className="page">
      <Crumbs items={[{ label: 'Home', to: '/' }, { label: 'Add a language' }]} />

      <header className="page-header">
        <h1>Add a language</h1>
        <p className="page-description">
          Pick one to put on your home screen. You can study more than one at a time — progress,
          streak and card setup are kept per language.
        </p>
      </header>

      {languages.loading && <Loading what="languages" />}
      {languages.error && <LoadError message={languages.error} onRetry={languages.reload} />}

      {languages.data && (
        <div className="card-grid">
          {languages.data.languages.map((language) => {
            const already = settings.studyingLanguages.includes(language.code);
            return (
              <button
                key={language.code}
                type="button"
                className="tile tile--button"
                onClick={() => add(language.code)}
                // Already on the home screen: still selectable, because tapping
                // it should take you home rather than do nothing, but marked so
                // the choice is not misleading.
                aria-current={already ? 'true' : undefined}
              >
                <span className="tile-emblem" aria-hidden="true">
                  {language.emblem}
                </span>
                <span className="tile-body">
                  <span className="tile-title">
                    {language.name}
                    <span className="tile-native">{language.nativeName}</span>
                  </span>
                  <span className="tile-blurb">{language.blurb}</span>
                  <span className="tile-meta">
                    {language.bookCount} {language.bookCount === 1 ? 'book' : 'books'}
                    {language.transliteration && ` · with ${language.transliteration.name}`}
                    {already && ' · already added'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {languages.data && languages.data.languages.length === 1 && (
        <p className="muted footnote">
          More languages are on the way. The app is built for them: a language that needs a
          romanization step — Pinyin, Rōmaji — gets it as a separate part of every card, which you
          can show or hide like any other.
        </p>
      )}
    </main>
  );
}
